import React, { useState } from 'react';
import { Modal, Form, Input, Select, Checkbox, DatePicker, Button } from 'antd';
import { timeConverter } from '../utils/TimeConverter';
import dayjs from 'dayjs';
import typeOptions from '../utils/typeOptions';

const { Option } = Select;

/**
 * Asset 編輯/新增 Modal 組件
 * @param {boolean} visible - Modal 是否可見
 * @param {Object} editingAsset - 要編輯的資產數據，為 null 時表示新增模式
 * @param {Function} onCancel - 關閉 Modal 的回調函數
 * @param {Function} onSubmit - 提交表單的回調函數
 */
const AssetModal = ({ visible, editingAsset, onCancel, onSubmit }) => {
  const [form] = Form.useForm();
  const [remainingDays, setRemainingDays] = useState(0);

  // 處理 is_live 變更
  const handleIsLiveChange = (checked) => {
    if (!checked) {
      form.setFieldValue('expire_time', null);
      setRemainingDays(0);
    }
  };

  // 處理提交
  const handleSubmit = async (values) => {
    const formData = { ...values };

    // 處理 is_live 和 expire_time 欄位
    const typeIndex = typeOptions.findIndex(option => option.value === formData.type);
    if (typeIndex >= 6) {
      formData.is_live = false;
      formData.expire_time = null;
    } else {
      if (!formData.is_live) {
        formData.is_live = false;
        formData.expire_time = null;
      } else if (formData.expire_time) {
        formData.expire_time = formData.expire_time.format();
      } else {
        formData.expire_time = null;
      }
    }

    if(formData.expire_time != null){
      formData.expire_time = timeConverter.localToUtc(formData.expire_time);
    }

    onSubmit(formData);
  };

  // 重設表單
  const calculateRemainingDays = (expireTime) => {
    if (expireTime) {
      const expire = dayjs(expireTime);
      if (expire.isValid()) {
        const days = expire.diff(dayjs(), 'day');
        setRemainingDays(days);
        return days;
      }
    }
    setRemainingDays(0);
    return 0;
  };

  React.useEffect(() => {
    if (visible) {
      form.resetFields();
      setRemainingDays(0);
      if (editingAsset) {
        const formData = { ...editingAsset };
        if (formData.expire_time) {
          formData.expire_time = dayjs(formData.expire_time);
          calculateRemainingDays(formData.expire_time);
        }
        form.setFieldsValue(formData);
      }
    }
  }, [visible, editingAsset, form]);

  const disabledDate = (current) => {
    const today = dayjs();
    const tenYearsLater = today.add(10, 'year');
    return current && current > tenYearsLater;
  };

  return (
    <Modal
      title={editingAsset ? "Edit asset" : "Add asset"}
      visible={visible}
      onCancel={onCancel}
      footer={null}
      destroyOnClose
    >
      <Form form={form} onFinish={handleSubmit} layout="vertical">
        <Form.Item name="name" label="Name" rules={[{ required: true, message: '請輸入資產名稱！' }]}>
          <Input />
        </Form.Item>
        <Form.Item name="type" label="Type" rules={[{ required: true, message: '請選擇資產類型！' }]}>
          <Select>
            {typeOptions.map(option => (
              <Option key={option.value} value={option.value}>{option.label}</Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name="uri" label="File Name">
          <Input />
        </Form.Item>
        <Form.Item name="file_uri" label="URL(PC)">
          <Input />
        </Form.Item>
        <Form.Item name="mobile_uri" label="URL(ANDROID)">
          <Input />
        </Form.Item>
        <Form.Item name="file_size" label="File Size (MB)">
          <Input />
        </Form.Item>
        <Form.Item name="stereo_l" label="Stereo_L">
          <Input type="number" />
        </Form.Item>
        <Form.Item name="fov" label="FOV">
          <Input type="number" />
        </Form.Item>
        <Form.Item name="image" label="Thumbnail (URL)">
          <Input />
        </Form.Item>
        
        <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => 
          prevValues.type !== currentValues.type
        }>
          {({ getFieldValue }) => {
            const currentType = getFieldValue('type');
            const typeIndex = typeOptions.findIndex(option => option.value === currentType);
            
            return typeIndex < 6 ? (
              <>
                <Form.Item name="is_live" label="is Live" valuePropName="checked" initialValue={true}>
                  <Checkbox onChange={(e) => handleIsLiveChange(e.target.checked)} />
                </Form.Item>
                
                <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => 
                  prevValues.is_live !== currentValues.is_live
                }>
                  {({ getFieldValue }) => {
                    const isLiveValue = getFieldValue('is_live');
                    
                    return isLiveValue ? (
                      <>
                        <Form.Item 
                          name="expire_time" 
                          label="到期時間"
                        >
                          <DatePicker 
                            showTime={{ format: 'HH:mm' }}
                            format="YYYY-MM-DD HH:mm"
                            disabledDate={disabledDate}
                            allowClear={true}
                            style={{ width: '100%' }}
                            onChange={(date, dateString) => {
                              if (!date) {
                                setRemainingDays(0);
                              } else {
                                calculateRemainingDays(date);
                              }
                            }}
                          />
                        </Form.Item>
                        <Form.Item label="剩餘天數">
                          <strong>{remainingDays} 天</strong>
                        </Form.Item>
                      </>
                    ) : null;
                  }}
                </Form.Item>
              </>
            ) : null;
          }}
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit">
            {editingAsset ? "Update asset" : "Add asset"}
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AssetModal;
